import React, { useEffect, useRef, useState } from 'react';
import Uppy from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import Webcam from '@uppy/webcam';
import ImageEditor from '@uppy/image-editor';
import Compressor from '@uppy/compressor';

// Import Uppy styles
import '@uppy/core/css/style.min.css';
import '@uppy/dashboard/css/style.min.css';
import '@uppy/webcam/css/style.min.css';
import '@uppy/image-editor/css/style.min.css';

export default function ImageUploader({
  onUpload,
  onError,
  maxFiles = 2,
  maxFileSize = 10 * 1024 * 1024, // 10MB
  allowedFileTypes = ['image/*'],
  enableWebcam = true,
  enableImageEditor = true,
  height = 400,
  note = 'Upload photos of your recipe card (front and back)',
}) {
  const dashboardRef = useRef(null);
  const uppyRef = useRef(null);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    const uppy = new Uppy({
      restrictions: {
        maxNumberOfFiles: maxFiles,
        maxFileSize: maxFileSize,
        allowedFileTypes: allowedFileTypes,
      },
      autoProceed: false,
    });

    // Add webcam plugin
    if (enableWebcam) {
      uppy.use(Webcam, {
        modes: ['picture'],
        mirror: false,
        showVideoSourceDropdown: true,
        preferredImageMimeType: 'image/jpeg',
      });
    }

    // Add image editor for cropping/rotating
    if (enableImageEditor) {
      uppy.use(ImageEditor, {
        quality: 0.9,
      });
    }

    // Compress images aggressively to reduce API token usage
    uppy.use(Compressor, {
      quality: 0.7,
      maxWidth: 1200,
      maxHeight: 1200,
    });

    // Add Dashboard plugin
    uppy.use(Dashboard, {
      target: dashboardRef.current,
      inline: true,
      height: height,
      width: '100%',
      note: note,
      proudlyDisplayPoweredByUppy: false,
      showProgressDetails: true,
      plugins: [
        ...(enableWebcam ? ['Webcam'] : []),
        ...(enableImageEditor ? ['ImageEditor'] : []),
      ],
    });

    // Handle file changes
    uppy.on('file-added', () => {
      const currentFiles = uppy.getFiles().map((file) => ({
        name: file.name,
        type: file.type,
        size: file.size,
        data: file.data,
      }));
      setFiles(currentFiles);
    });

    uppy.on('file-removed', () => {
      const currentFiles = uppy.getFiles().map((file) => ({
        name: file.name,
        type: file.type,
        size: file.size,
        data: file.data,
      }));
      setFiles(currentFiles);
    });

    uppy.on('error', (error) => {
      console.error('Uppy error:', error);
      onError?.(error.message || 'Upload failed');
    });

    uppyRef.current = uppy;

    return () => {
      uppy.destroy();
    };
  }, [maxFiles, maxFileSize, enableWebcam, enableImageEditor, height, note, onError]);

  // Notify parent when files change
  useEffect(() => {
    if (files.length > 0) {
      onUpload?.(files);
    }
  }, [files, onUpload]);

  return <div ref={dashboardRef} />;
}
